/**
 * Dashboard quick-start card for returning learners.
 *
 * Provides a one-click path into module selection for the default
 * Goethe A1 simulation setup.
 *
 * @module QuickStartExamCard
 */

import { Link, useNavigate } from 'react-router-dom';
import { Lock, Rocket } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { QuickExamStartTarget } from '@/hooks/useQuickExamStartHref';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { canStartSimulation, SIMULATION_RENEWAL_BLOCK_MESSAGE } from '@/lib/planAccess';
import { ROUTES } from '@/lib/routeConstants';

export interface QuickStartExamCardProps {
  readonly quickExamStart: QuickExamStartTarget;
}

export function QuickStartExamCard({ quickExamStart }: QuickStartExamCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, isLoading } = useCurrentPlan();
  const simulationAllowed = canStartSimulation(plan, user?.userType === 'admin');
  const { href: quickStartHref, examType: quickStartExamType, level: quickStartLevel } =
    quickExamStart;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>
              {t('dashboard.quickStart.title', { defaultValue: 'Quick exam start' })}
            </CardTitle>
            <CardDescription>
              {!isLoading && !simulationAllowed
                ? t('dashboard.quickStart.renewRequired', {
                    defaultValue: SIMULATION_RENEWAL_BLOCK_MESSAGE,
                  })
                : t('dashboard.quickStart.description', {
                    defaultValue: 'Jump directly to module selection and begin faster.',
                  })}
            </CardDescription>
          </div>
          <Badge variant="secondary">{`${quickStartExamType.toUpperCase()} · ${quickStartLevel}`}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Button className="w-full sm:w-auto" disabled>
            <Rocket className="mr-2 h-4 w-4" />
            {t('dashboard.quickStart.cta', { defaultValue: 'Start simulation' })}
          </Button>
        ) : simulationAllowed ? (
          <Button asChild className="w-full sm:w-auto">
            <Link to={quickStartHref}>
              <Rocket className="mr-2 h-4 w-4" />
              {t('dashboard.quickStart.cta', { defaultValue: 'Start simulation' })}
            </Link>
          </Button>
        ) : (
          <Button
            type="button"
            className="w-full sm:w-auto"
            onClick={() => navigate(`${ROUTES.SETTINGS}#plan`)}
          >
            <Lock className="mr-2 h-4 w-4" />
            {t('dashboard.planBanner.expired.cta', { defaultValue: 'Renew plan' })}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
